/**
 * MenuButton renders a full-width menu item button with icon and focus styling.
 *
 * Why: Menu dialogs (RoomMenu, etc.) have buttons with consistent styling:
 * full width, left-aligned icon, focus ring from theme colors. This component
 * captures that pattern to reduce repetition.
 *
 * When to use: Use in menu dialogs for action buttons. Pass `isFocused` based
 * on the navigation state to get proper focus ring styling.
 */
import * as React from 'react'
import { Button } from '@merels/ui'
import { cn } from '@merels/ui'
import { useTheme } from '../../../contexts/ThemeContext'

export interface MenuButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  /** Icon component to render before the text */
  icon?: React.ComponentType<{ width: number; height: number }>
  /** Whether this button is currently focused (for focus ring styling) */
  isFocused?: boolean
  /** Button variant */
  variant?: 'outline' | 'default'
  /** Test ID for E2E tests */
  testId?: string
}

export const MenuButton = React.forwardRef<HTMLButtonElement, MenuButtonProps>(
  ({ icon: Icon, isFocused, variant = 'outline', testId, className, children, ...props }, ref) => {
    const { getAppColor } = useTheme()
    const appColorShades = getAppColor()
    const accentColor = appColorShades.main
    const backgroundColor = appColorShades.background

    return (
      <Button
        ref={ref}
        variant={variant}
        data-testid={testId}
        className={cn(
          "w-full justify-start gap-2 h-11 text-base [&_svg]:!size-[18px]",
          isFocused && "outline-none",
          className
        )}
        style={isFocused && accentColor ? {
          boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
        } : undefined}
        tabIndex={isFocused ? 0 : -1}
        {...props}
      >
        {Icon && <Icon width={18} height={18} />}
        {children}
      </Button>
    )
  }
)
MenuButton.displayName = 'MenuButton'
